/**
 * Pipeline Zustand store — manages the AI-powered idea validation pipeline state.
 */

import { create } from 'zustand';
import { api, extractApiError } from '../api/client';

// ── Types ─────────────────────────────────────────────────────

export interface EarlyAdopterPersona {
  name?: string;
  age?: string;
  job?: string;
  frustration?: string;
  goal?: string;
  whereToFind?: string;
  quote?: string;
}

export interface PositioningAI {
  targetProfile?: string;
  valueProposition?: string;
  painPointFocus?: string;
  earlyAdopterPersona?: EarlyAdopterPersona;
  positioningStatement?: string;
  elevatorPitch?: string;
  uniqueMechanism?: string;
  positioningBlueprint?: string;
}

export interface Pipeline {
  id: string;
  owner: string;
  status: 'active' | 'incubator' | 'completed' | 'killed';
  currentStep: number;
  userProfile?: {
    technicalSkill?: string;
    budget?: string;
    timeAvailable?: string;
    previousExperience?: string;
    creatorType?: string;
    ideasInDrawer?: number;
    completedAt?: string;
    aiOutput?: Record<string, unknown>;
  };
  idea?: {
    rawIdea?: string;
    guidedAnswers?: {
      problemSolved?: string;
      whoSuffers?: string;
      currentSolution?: string;
      promisedResult?: string;
      budgetAndTime?: string;
    };
    aiOutput?: Record<string, unknown>;
    completedAt?: string;
  };
  realityCheck?: {
    braveData?: Record<string, unknown>;
    aiOutput?: Record<string, unknown>;
    completedAt?: string;
  };
  positioning?: {
    suggestedNiches?: Record<string, unknown>[];
    differentiationAngles?: Record<string, unknown>[];
    monetizationModels?: Record<string, unknown>[];
    userChoices?: {
      selectedNiche?: string;
      selectedDifferentiation?: string;
      selectedMonetization?: string;
    };
    aiOutput?: PositioningAI & Record<string, unknown>;
    completedAt?: string;
  };
  validationPath?: {
    aiOutput?: Record<string, unknown>;
    userResults?: {
      visits?: number;
      conversions?: number;
      emailsCollected?: number;
      preOrders?: number;
      qualitativeFeedback?: string;
      submittedAt?: string;
    };
    completedAt?: string;
  };
  paymentTest?: {
    aiOutput?: Record<string, unknown>;
    completedAt?: string;
  };
  mvpBlueprint?: {
    aiOutput?: Record<string, unknown>;
    completedAt?: string;
  };
  title?: string;
  linkedStartupId?: string;
  isFirstPipeline?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PipelineState {
  pipeline: Pipeline | null;
  allPipelines: {
    active: Pipeline | null;
    incubator: Pipeline[];
    completed: Pipeline[];
    killed: Pipeline[];
  };
  loading: boolean;
  error: string | null;

  loadPipelines: () => Promise<void>;
  loadPipeline: (id: string) => Promise<void>;
  createPipeline: () => Promise<Pipeline>;
  updatePipeline: (pipeline: Pipeline) => void;
  clearError: () => void;
}

export const usePipelineStore = create<PipelineState>((set) => ({
  pipeline: null,
  allPipelines: { active: null, incubator: [], completed: [], killed: [] },
  loading: false,
  error: null,

  loadPipelines: async () => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.get('/pipeline');
      set({
        allPipelines: {
          active: data.data.active,
          incubator: data.data.incubator ?? [],
          completed: data.data.completed ?? [],
          killed: data.data.killed ?? [],
        },
        loading: false,
      });
    } catch (err) {
      set({ error: extractApiError(err).message, loading: false });
    }
  },

  loadPipeline: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.get(`/pipeline/${id}`);
      set({ pipeline: data.data.pipeline, loading: false });
    } catch (err) {
      set({ error: extractApiError(err).message, loading: false });
    }
  },

  createPipeline: async () => {
    try {
      const { data } = await api.post('/pipeline');
      const pipeline = data?.data?.pipeline as Pipeline;
      if (!pipeline?.id) {
        const err = new Error('Failed to create pipeline — server returned invalid data. Please try again.');
        set({ error: err.message });
        throw err;
      }
      set({ pipeline, error: null });
      return pipeline;
    } catch (err) {
      const msg = extractApiError(err).message;
      set({ error: msg });
      throw err;
    }
  },

  updatePipeline: (data: Pipeline) => {
    // Handle both { pipeline: {...} } wrapper and direct pipeline object
    const pipeline = (data as unknown as { pipeline?: Pipeline })?.pipeline ?? data;
    if (!pipeline?.id) {
      console.error('[Store] updatePipeline received invalid data:', data);
      set({ error: 'Received invalid pipeline data. Please reload the page.' });
      return;
    }
    // Guard: clamp currentStep to MAX_STEPS (5)
    if (pipeline.currentStep > 5) pipeline.currentStep = 5;
    console.log('[Store] Updating pipeline id:', pipeline.id, 'currentStep:', pipeline.currentStep);
    set({ pipeline, error: null });
  },

  clearError: () => set({ error: null }),
}));
